import { useDynamicIcon } from '@/lib/core';
import { ComponentProps } from 'react';

export interface IconProps extends Omit<ComponentProps<'svg'>, 'ref'> {
  /**
   * Icon name in kebab-case or PascalCase
   * @example 'info-circle-solid', 'check-circle-solid', 'InfoCircleSolid'
   */
  name: string;
  /**
   * Icon width
   * @default '1.5em'
   */
  width?: number | string;
  /**
   * Icon height
   * @default '1.5em'
   */
  height?: number | string;
  /**
   * Stroke width
   * @default 1.5
   */
  strokeWidth?: number;
  /**
   * Icon color (defaults to currentColor)
   */
  color?: string;
}

/**
 * Dynamic icon component that loads iconoir-react icons on demand
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Icon name="info-circle-solid" />
 *
 * // With custom props
 * <Icon name="check-circle-solid" width={24} height={24} className="text-green-600" />
 *
 * // Works with any iconoir icon
 * <Icon name="airplane-helix-45deg" color="blue" strokeWidth={2} />
 * ```
 */
export function Icon({ name, width, height, strokeWidth, color, className, ...props }: IconProps) {
  const IconComponent = useDynamicIcon(name);

  if (!IconComponent) {
    return null;
  }

  return (
    <IconComponent
      width={width}
      height={height}
      strokeWidth={strokeWidth}
      color={color}
      className={className}
      {...props}
    />
  );
}
